import React from 'react'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'
import { ReactComponent as TablerDockIcon } from 'assets/icons/tabler-dock.svg'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import { ReactComponent as PenIcon } from 'assets/icons/pen_duacolor.svg'
import { ReactComponent as ParentsIcon } from 'assets/icons/parents.svg'
import { ReactComponent as SendCircleIcon } from 'assets/icons/send_circle.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as ArrowIcon } from 'assets/icons/arrow_short_right.svg'
import CheckBox from 'shared/ui/CheckBox/CheckBox'
import StudentAccordion from './components/StudentAccordion/StudentAccordion'
import AssignActivityModal from 'widgets/AssignActivityModal/AssignActivityModal'
import s from './styles/DetailsModalAssignment.module.scss'
import useDetailsModalAssignment from './hooks/useDetailsModalAssignment'

const DetailsModalAssignment = observer(
	({ isOpen, onClose, assignment, source = 'classroom', onAssignmentSynced }) => {
	const {
		modalRef,
		onBackdropMouseDown,
		displayStudents,
		activeFilter,
		areAllSelected,
		selectedCount,
		openAccordions,
		filterButtons,
		handleFilterChange,
		handleSelectAll,
		handleClearSelection,
		handleAccordionToggle,
		isCompleted,
		dueDate,
		dueDateText,
		assignmentTitle,
		isEditModalOpen,
		setIsEditModalOpen,
		isMobile,
	} = useDetailsModalAssignment({
		isOpen,
		onClose,
		assignment,
		source,
		onAssignmentSynced,
	})

	if (!isOpen) return null

	return createPortal(
		<div
			className={s.overlay}
			role="dialog"
			aria-modal="true"
			aria-label="Assignment Details"
		>
			<div className={s.backdrop} onMouseDown={onBackdropMouseDown} />

			<div ref={modalRef} className={s.modal}>
				<div className={s.header}>
					<div className={s.header__inner}>
						{!isMobile && (
							<span className={s.header__icon}>
								<TablerDockIcon />
							</span>
						)}
						<div className={s.header__info}>
							<div className={s.header__top}>
								{isMobile && (
									<span className={s.header__icon}>
										<TablerDockIcon />
									</span>
								)}
								<span className={s.header__title}>{assignmentTitle}</span>
								{isMobile ? (
									<button
										type="button"
										className={s.closeBtn}
										onClick={onClose}
										aria-label="Close"
									>
										<span className={s.closeBtn__icon}>
											<CrossIcon />
										</span>
									</button>
								) : (
									<div className={s.actions}>
										<button
											className={s.edit}
											type="button"
											onClick={() => setIsEditModalOpen(true)}
										>
											<span className={s.edit__icon}>
												<PenIcon />
											</span>
											<span className={s.edit__text}>Edit</span>
										</button>
										<button
											type="button"
											className={s.closeBtn}
											onClick={onClose}
											aria-label="Close"
										>
											<span className={s.closeBtn__icon}>
												<CrossIcon />
											</span>
										</button>
									</div>
								)}
							</div>
							<div className={s.header__list}>
								<span
									className={`${s.badge} ${isCompleted ? s.completed : s.active}`}
								>
									{isCompleted ? 'Completed' : 'Active'}
								</span>
								{dueDate && (
									<span className={`${s.badge} ${s.due}`}>
										<span className={s.badge__icon}>
											<CalendarIcon />
										</span>
										<span>{dueDateText}</span>
									</span>
								)}
								{isMobile && (
									<button
										className={s.edit}
										type="button"
										onClick={() => setIsEditModalOpen(true)}
									>
										<span className={s.edit__icon}>
											<PenIcon />
										</span>
										<span className={s.edit__text}>Edit</span>
									</button>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className={s.body}>
					<div className={s.body__head}>
						<span className={s.body__title}>Student Details</span>
					</div>

					<div className={s.filters}>
						<div
							className={`${s.filters__inner} ${selectedCount > 0 ? s.selected : ''}`}
						>
							<div className={s.filters__tabs}>
								{filterButtons.map(button => (
									<button
										key={button.id}
										type="button"
										className={`${s.filters__tab} ${activeFilter === button.id ? s.active : ''} ${selectedCount > 0 ? s.selected : ''}`}
										onClick={() => handleFilterChange(button.id)}
									>
										{button.label} ({button.count})
									</button>
								))}
							</div>
							{selectedCount > 0 ? (
								<div className={s.filters__actions}>
									<div className={s.parents}>
										<span className={s.parents__trigger}>
											<span className={s.parents__icon}>
												<ParentsIcon />
											</span>
											<span className={s.parents__label}>Parents</span>
											<span className={s.parents__arrow}>
												<ArrowIcon />
											</span>
										</span>
									</div>
									<button type="button" className={s.send}>
										<span className={s.send__text}>Send Message</span>
										<span className={s.send__icon}>
											<SendCircleIcon />
										</span>
									</button>
								</div>
							) : (
								<div className={s.select} onClick={handleSelectAll}>
									<span className={s.select__text}>Select All</span>
									<CheckBox
										isSelected={areAllSelected}
										onToggle={handleSelectAll}
										variant="checkmark"
										className={s.select__checkbox}
										classNameLabel={s.select__label}
									/>
								</div>
							)}
						</div>

						{selectedCount > 0 && (
							<div className={s.filters__info}>
								<div className={s.selectionBar}>
									<span className={s.selectionBar__count}>
										{selectedCount} Selected
									</span>
									<button
										type="button"
										className={s.clear}
										onClick={handleClearSelection}
									>
										<span className={s.clear__icon}>
											<CrossIcon />
										</span>
										<span className={s.clear__text}>Clear selection</span>
									</button>
								</div>
								<div className={s.select} onClick={handleSelectAll}>
									<span className={s.select__text}>Select All</span>
									<CheckBox
										isSelected={areAllSelected}
										onToggle={handleSelectAll}
										variant="checkmark"
										className={s.select__checkbox}
										classNameLabel={s.select__label}
									/>
								</div>
							</div>
						)}
					</div>

					<div className={s.studentsList}>
						{displayStudents.length > 0 ? (
							displayStudents.map((student, index) => (
								<StudentAccordion
									key={student.id || index}
									student={student}
									assignment={assignment}
									isOpen={openAccordions.has(index)}
									onToggle={() => handleAccordionToggle(index)}
								/>
							))
						) : (
							<div className={s.studentsList__empty}>
								<p className={s.studentsList__emptyText}>No students found</p>
							</div>
						)}
					</div>
				</div>
			</div>

			<AssignActivityModal
				isOpen={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				editMode={true}
				assignment={assignment}
				onSuccess={onClose}
			/>
		</div>,
		document.body,
	)
})

export default DetailsModalAssignment
